// SkipInventoryCreatePage — /skip/inventory/create. Admin-only route.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { useCreateSkipInventoryItem } from '../hooks/useSkipInventory';
import {
  useSkipStockSizes,
  useSkipStockTypes,
} from '../settings/hooks/useSkipStock';
import { SkipUnitNumberInput } from './components/SkipUnitNumberInput';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipInventoryCreatePage.module.css';

interface FormState {
  skip_number: string;
  skip_size_id: string;
  stock_type_enc_id: string;
  skip_condition: 'excellent' | 'good' | 'fair' | 'poor' | 'repair_required';
  current_location: string;
  last_service_date: string;
  next_service_date: string;
  notes: string;
}

const EMPTY: FormState = {
  skip_number: '',
  skip_size_id: '',
  stock_type_enc_id: '',
  skip_condition: 'good',
  current_location: '',
  last_service_date: '',
  next_service_date: '',
  notes: '',
};

export function SkipInventoryCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // forSelection: true → server hides globals the tenant hasn't
  // activated, so the dropdown only ever offers usable options.
  const typesQuery = useSkipStockTypes({ forSelection: true });
  const sizesQuery = useSkipStockSizes(form.stock_type_enc_id || null);
  const create = useCreateSkipInventoryItem();

  const stockTypes = typesQuery.data?.stockTypes ?? [];
  const sizes = sizesQuery.data?.sizes ?? [];

  const selectedType = useMemo(
    () => stockTypes.find((t) => t.enc_id === form.stock_type_enc_id) ?? null,
    [stockTypes, form.stock_type_enc_id],
  );

  // Group the dropdown into Standard (global) + Custom (own-tenant)
  // sections. We only render the labelled separators when both groups
  // exist so single-section tenants stay clean.
  const standardTypes = useMemo(
    () => stockTypes.filter((t) => t.is_global),
    [stockTypes],
  );
  const customTypes = useMemo(
    () => stockTypes.filter((t) => !t.is_global),
    [stockTypes],
  );
  const showGroupLabels = standardTypes.length > 0 && customTypes.length > 0;

  // Reset size choice when stock type changes.
  useEffect(() => {
    setForm((f) => (f.skip_size_id ? { ...f, skip_size_id: '' } : f));
  }, [form.stock_type_enc_id]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.skip_number || !form.skip_size_id) {
      setError('Unit number, stock type and size are required.');
      return;
    }
    const body: Record<string, unknown> = {
      skip_number: form.skip_number,
      skip_size_id: form.skip_size_id,
      skip_condition: form.skip_condition,
      current_location: form.current_location || null,
      notes: form.notes || null,
    };
    if (form.last_service_date) body['last_service_date'] = form.last_service_date;
    if (form.next_service_date) body['next_service_date'] = form.next_service_date;

    create.mutate(body, {
      onSuccess: (data) =>
        navigate(`/skip/inventory/${encodeURIComponent(data.item.enc_id)}`),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Add Inventory Unit"
        subtitle="Add a new unit to the fleet"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/inventory')}
          >
            Back to Inventory
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className={styles.form}>
        <Card>
          <h2 className={styles.sectionHeading}>Unit Details</h2>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Stock Type</span>
              <select
                required
                value={form.stock_type_enc_id}
                onChange={(e) =>
                  setForm({ ...form, stock_type_enc_id: e.target.value, skip_size_id: '' })
                }
                aria-label="Stock Type"
              >
                <option value="">— Select —</option>
                {showGroupLabels ? (
                  <>
                    <optgroup label="── Standard ──">
                      {standardTypes.map((t) => (
                        <option key={t.enc_id} value={t.enc_id}>
                          {t.name} [{t.prefix}]
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="── Custom ──">
                      {customTypes.map((t) => (
                        <option key={t.enc_id} value={t.enc_id}>
                          {t.name} [{t.prefix}]
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  stockTypes.map((t) => (
                    <option key={t.enc_id} value={t.enc_id}>
                      {t.name} [{t.prefix}]
                    </option>
                  ))
                )}
              </select>
            </label>
            <label className={styles.field}>
              <span>Size</span>
              <select
                required
                disabled={!form.stock_type_enc_id}
                value={form.skip_size_id}
                onChange={(e) => setForm({ ...form, skip_size_id: e.target.value })}
                aria-label="Size"
              >
                <option value="">
                  {form.stock_type_enc_id ? '— Select —' : 'Select stock type first'}
                </option>
                {sizes.map((s) => (
                  <option key={s.enc_id} value={s.enc_id}>
                    {s.name}
                    {s.typical_capacity ? ` (${s.typical_capacity})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Unit Number</span>
              <SkipUnitNumberInput
                stockTypePrefix={selectedType?.prefix ?? null}
                value={form.skip_number}
                onChange={(v) => setForm({ ...form, skip_number: v })}
              />
            </label>
            <label className={styles.field}>
              <span>Condition</span>
              <select
                value={form.skip_condition}
                onChange={(e) =>
                  setForm({
                    ...form,
                    skip_condition: e.target.value as FormState['skip_condition'],
                  })
                }
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="repair_required">Repair required</option>
              </select>
            </label>
            <label className={styles.field}>
              <span>Current Location</span>
              <input
                value={form.current_location}
                onChange={(e) => setForm({ ...form, current_location: e.target.value })}
                placeholder="Yard / depot location"
              />
            </label>
            <label className={styles.field}>
              <span>Last Service</span>
              <input
                type="date"
                value={form.last_service_date}
                onChange={(e) => setForm({ ...form, last_service_date: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Next Service</span>
              <input
                type="date"
                value={form.next_service_date}
                onChange={(e) => setForm({ ...form, next_service_date: e.target.value })}
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>Notes</span>
            <textarea
              rows={3}
              maxLength={5000}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/skip/inventory')}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Unit'}
          </Button>
        </div>
      </form>
    </div>
  );
}
